import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { agency, clients as clientsApi } from "../api";
import { HealthBig } from "../components/HealthScore";
import NarrativeReader from "../components/NarrativeReader";
import RecommendationCard from "../components/RecommendationCard";
import GeoSnapshot from "../components/GeoSnapshot";
import MetricChart from "../components/MetricChart";
import DataSourceStatus from "../components/DataSourceStatus";
import MetricTile, { formatNumber } from "../components/MetricTile";
import DataTable from "../components/DataTable";
import PageSpeedCard from "../components/PageSpeedCard";
import CrawlIssuesCard from "../components/CrawlIssuesCard";
import DeviceBreakdown from "../components/DeviceBreakdown";

/**
 * Agency-side client detail.
 *
 * Rework: data-first. Top row of KPI tiles. Data panels
 * (top queries, top pages, device, PageSpeed, crawl) take the primary
 * area. Narrative + recommendations live in a sidebar.
 */
export default function AgencyClientDetail() {
  const { clientId } = useParams();
  const id = parseInt(clientId, 10);
  const [all, setAll] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(null);

  async function load() {
    setErr(null);
    try {
      const [clientList, overview, health, narrative, recos, ds, visibility, engagementS, conversionS] = await Promise.all([
        agency.listClients(),
        clientsApi.overview(id),
        clientsApi.health(id).catch(() => ({ current: null, history: [] })),
        clientsApi.narrative(id).catch(() => null),
        agency.recommendations({ client_id: id }).catch(() => []),
        clientsApi.dataSources(id).catch(() => []),
        clientsApi.dataVisibility(id).catch(() => ({ series: [] })),
        clientsApi.dataEngagement(id).catch(() => ({ series: [] })),
        clientsApi.dataConversion(id).catch(() => ({ series: [] })),
      ]);
      const client = clientList.find((c) => c.id === id);
      setAll({ client, overview, health, narrative, recos, ds, visibility, engagementS, conversionS });
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function onStatusChange(recoId, body) {
    await agency.updateRecommendation(recoId, body);
  }

  async function triggerNarrate() {
    if (!window.confirm("Generate a fresh monthly narrative now? Takes ~60s and uses API credits.")) return;
    setBusy("narrate");
    try { await agency.triggerNarrate(id); await load(); }
    catch (e) { alert(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  }

  // NOTE: the /collect endpoint is a stub. Cron is the only path that
  // actually fires collectors today. Wire to a task runner (Stage 7) before
  // re-enabling the button.

  if (err) return <div className="page"><div className="error-box">{err}</div></div>;
  if (!all) return <div className="page"><p className="subtle">Loading…</p></div>;
  if (!all.client) return <div className="page"><div className="error-box">Client not found.</div></div>;

  const { client, overview, health, narrative, recos, ds, visibility, engagementS, conversionS } = all;

  const vis = overview.visibility;
  const eng = overview.engagement;
  const conv = overview.conversion;
  const geo = overview.geo;
  const site = overview.site_health;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{client.name}</h1>
          <div className="subtle">
            {client.industry || "—"} • {client.location || "—"} •{" "}
            {client.website_url ? <a href={client.website_url} target="_blank" rel="noreferrer">{client.website_url}</a> : "no url"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="secondary"
            disabled
            title="Automatic collection runs on a daily schedule. Manual trigger from the dashboard ships in Stage 7."
          >
            Collect (auto)
          </button>
          <button disabled={busy} onClick={triggerNarrate}>
            {busy === "narrate" ? "Generating…" : "Generate narrative"}
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      <section className="tiles">
        <MetricTile
          label="Organic sessions"
          value={formatNumber(vis.ga4_organic.current.organic_sessions)}
          deltaPct={vis.ga4_organic.delta_pct.sessions}
          footnote={`${formatNumber(vis.gsc.current.clicks)} GSC clicks / ${formatNumber(vis.gsc.current.impressions)} impressions`}
        />
        <MetricTile
          label="Engagement rate"
          value={`${eng.ga4.current.engagement_rate_pct.toFixed(0)}%`}
          deltaPct={eng.ga4.delta_pct.engagement_rate}
          footnote={`${Math.round(eng.ga4.current.avg_session_duration_sec)}s avg session`}
        />
        <MetricTile
          label="Conversions"
          value={formatNumber(conv.ga4.current.conversions)}
          deltaPct={conv.ga4.delta_pct.conversions}
          footnote={`${conv.ga4.current.conversion_rate_pct.toFixed(2)}% rate`}
        />
        <MetricTile
          label="GEO visibility"
          value={geo.available ? `${geo.score_pct.toFixed(1)}%` : "—"}
          deltaPct={null}
          footnote={geo.available ? `${geo.total_score} / ${geo.max_possible} pts` : "no snapshot"}
        />
      </section>

      <div className="split" style={{ marginTop: 20 }}>
        {/* ---------------- Left: data panels ---------------- */}
        <div>
          <div className="card">
            <h2>Trends</h2>
            <div className="grid grid-3">
              <MetricChart title="Organic sessions" series={visibility.series} />
              <MetricChart title="Engagement %" series={engagementS.series} color="#4c7b4c" />
              <MetricChart title="Conversions" series={conversionS.series} color="#3e6e93" />
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Top search queries (GSC)</h2>
            <DataTable
              empty="No GSC queries in this period."
              rows={vis.top_queries}
              columns={[
                { key: "query", label: "Query" },
                { key: "clicks", label: "Clicks", align: "right",
                  render: (r) => r.clicks.toLocaleString() },
                { key: "impressions", label: "Impressions", align: "right",
                  render: (r) => r.impressions.toLocaleString() },
                { key: "avg_position", label: "Avg pos", align: "right",
                  render: (r) => r.avg_position.toFixed(1) },
              ]}
            />
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Top landing pages (GA4)</h2>
            <DataTable
              empty="No landing page data in this period."
              rows={eng.top_landing_pages_current}
              columns={[
                { key: "page", label: "Page",
                  render: (r) => <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "inline-block", maxWidth: 340 }}>{r.page}</span> },
                { key: "sessions", label: "Sessions", align: "right",
                  render: (r) => r.sessions.toLocaleString() },
                { key: "engagement_rate_pct", label: "Engaged", align: "right",
                  render: (r) => `${r.engagement_rate_pct.toFixed(0)}%` },
                { key: "conversions", label: "Conversions", align: "right",
                  render: (r) => r.conversions.toLocaleString() },
              ]}
            />
          </div>

          <div className="grid grid-2" style={{ marginTop: 14 }}>
            <div className="card">
              <h2>By device</h2>
              <DeviceBreakdown rows={eng.by_device_current} mode="agency" />
            </div>
            <div className="card">
              <h2>Top cities</h2>
              <DataTable
                empty="No city-level GA4 data."
                rows={eng.by_city_current?.slice(0, 8)}
                columns={[
                  { key: "city", label: "City" },
                  { key: "sessions", label: "Sessions", align: "right",
                    render: (r) => r.sessions.toLocaleString() },
                ]}
              />
            </div>
          </div>

          <div className="grid grid-2" style={{ marginTop: 14 }}>
            <div className="card">
              <h2>PageSpeed</h2>
              <PageSpeedCard pagespeed={site.pagespeed_current} />
            </div>
            <div className="card">
              <h2>Crawl issues</h2>
              <CrawlIssuesCard crawl={site.crawl} />
            </div>
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>GEO snapshot</h2>
            <GeoSnapshot snapshot={overview.geo.available ? {
              month: overview.geo.month,
              total_score: overview.geo.total_score,
              max_possible: overview.geo.max_possible,
              score_pct: overview.geo.score_pct,
              chatgpt_score: overview.geo.by_platform.chatgpt,
              gemini_score: overview.geo.by_platform.gemini,
              perplexity_score: overview.geo.by_platform.perplexity,
              google_aio_score: overview.geo.by_platform.google_aio,
              category_scores: overview.geo.by_category,
              top_gaps: overview.geo.top_gaps,
              competitors_summary: overview.geo.competitors_surfacing,
              previous_month_score: overview.geo.previous_month_score,
            } : null} mode="agency" />
          </div>
        </div>

        {/* ---------------- Right: narrative + recs + status ---------------- */}
        <div>
          <div className="card">
            <h2>Health</h2>
            <HealthBig snapshot={health.current} />
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Narrative</h2>
            {narrative ? (
              <>
                <p className="headline" style={{ margin: "0 0 10px", fontSize: 16 }}>{narrative.headline}</p>
                <details className="disclosure">
                  <summary>Read the client-facing narrative</summary>
                  <NarrativeReader narrative={narrative.client_narrative} />
                </details>
                {narrative.agency_addendum && (
                  <details className="disclosure" style={{ marginTop: 10 }}>
                    <summary>Agency addendum (internal)</summary>
                    <AgencyAddendumView addendum={narrative.agency_addendum} />
                  </details>
                )}
              </>
            ) : (
              <div className="empty">No narrative yet. Click "Generate narrative" above.</div>
            )}
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Recommendations</h2>
            {recos.length === 0 ? (
              <div className="empty">No recommendations yet.</div>
            ) : recos.map((r) => (
              <RecommendationCard key={r.id} reco={r} onStatusChange={onStatusChange} />
            ))}
          </div>

          <div className="card" style={{ marginTop: 14 }}>
            <h2>Data sources</h2>
            <DataSourceStatus sources={ds} mode="agency" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AgencyAddendumView({ addendum }) {
  if (!addendum) return <div className="empty">No agency addendum.</div>;
  return (
    <div>
      {addendum.headline_for_rps && (
        <p style={{ fontWeight: 600, fontSize: 15 }}>{addendum.headline_for_rps}</p>
      )}
      {addendum.estimated_revenue_impact && (
        <div className="card" style={{ background: "var(--surface-2)", boxShadow: "none", marginBottom: 12 }}>
          <h3 style={{ margin: 0, textTransform: "none", letterSpacing: 0, fontSize: 14 }}>
            Revenue impact: {addendum.estimated_revenue_impact.range_usd}
          </h3>
          <p style={{ margin: "6px 0 0", fontSize: 13 }}>{addendum.estimated_revenue_impact.justification}</p>
        </div>
      )}
      {["opportunities", "upsell_signals", "geo_recommendations", "anomalies"].map((key) =>
        Array.isArray(addendum[key]) && addendum[key].length > 0 && (
          <div key={key} className="section" style={{ marginBottom: 14 }}>
            <div className="section-label">{key.replaceAll("_", " ")}</div>
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {addendum[key].map((item, i) => (
                <li key={i} style={{ marginBottom: 6, fontSize: 13 }}>
                  <strong>{item.title}</strong>
                  {(item.description || item.detail) && (
                    <div className="body" style={{ marginTop: 2, fontSize: 13 }}>{item.description || item.detail}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </div>
  );
}
